import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  BulbOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  RetweetOutlined,
  RobotOutlined,
  SearchOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import Layout from '../components/Layout';
import api from '../services/api';
import { Test, TestDifficulty, User } from '../types';
import { TEST_SUBGROUP_OPTIONS } from '../constants/testSubgroups';
import {
  TEST_DIFFICULTY_COLOR,
  TEST_DIFFICULTY_LABEL,
  TEST_DIFFICULTY_OPTIONS,
} from '../constants/testDifficulty';
import { getApiErrorMessage } from '../utils/apiError';
import { AI_TEST_GENERATION_ENABLED } from '../config/features';
import './AdminAssignments.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type CreateTestMode = 'manual' | 'text' | 'excel' | 'ai';

// Режим 'ai' остаётся в типе и в коде, но в список не попадает, пока
// возможность выключена флагом AI_TEST_GENERATION_ENABLED.
const CREATE_TEST_MODE_ORDER: CreateTestMode[] = AI_TEST_GENERATION_ENABLED
  ? ['manual', 'text', 'excel', 'ai']
  : ['manual', 'text', 'excel'];

interface ManualTestFormValues {
  title: string;
  description?: string;
  block: 'products' | 'vehicles';
  subgroup?: string;
  difficulty: TestDifficulty;
  time_limit_minutes: number;
}

interface AdminTest extends Test {
  question_texts?: string[];
}

interface AdminAttempt {
  id: number;
  test_id: number;
  test_title?: string;
  user_id: number;
  user_name?: string;
  user_region?: string | null;
  user_department?: string | null;
  user_position?: string | null;
  started_at: string;
  completed_at?: string | null;
  score?: number | null;
  is_passed?: boolean | null;
  is_completed: boolean;
}

interface AdminAssignment {
  id: number;
  user_id: number;
  test_id: number;
  status: string;
  is_active: boolean;
  assigned_at: string;
  completed_at?: string | null;
}

interface DepartmentGroup {
  name: string;
  users: User[];
  regions: string[];
}

const normalize = (value: unknown) => String(value ?? '').trim().toLocaleLowerCase('ru');

const getInitials = (name: string) => name
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join('') || '—';

const getTestCategory = (test: AdminTest) => {
  if (test.subgroup?.trim()) return test.subgroup.trim();
  return test.block === 'vehicles' ? 'Модели автомобилей' : 'Ассортимент и запчасти';
};

const getQuestionWord = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return 'вопросов';
  if (last === 1) return 'вопрос';
  if (last >= 2 && last <= 4) return 'вопроса';
  return 'вопросов';
};

const AdminAssignments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [manualTestForm] = Form.useForm<ManualTestFormValues>();
  const [tests, setTests] = useState<AdminTest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [attempts, setAttempts] = useState<AdminAttempt[]>([]);
  const [assignments, setAssignments] = useState<AdminAssignment[]>([]);
  const [testSubgroupOptions, setTestSubgroupOptions] = useState<string[]>(
    [...TEST_SUBGROUP_OPTIONS],
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTestIds, setSelectedTestIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [testSearch, setTestSearch] = useState('');
  const [testCategory, setTestCategory] = useState('all');
  const [retakeTestSearch, setRetakeTestSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [retakeOnly, setRetakeOnly] = useState(false);
  const [deadline, setDeadline] = useState<Dayjs | null>(() => dayjs().add(14, 'day'));
  const [publishNow, setPublishNow] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateTestMode>('manual');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [textImportValue, setTextImportValue] = useState('');
  const [textImportBlock, setTextImportBlock] = useState<'products' | 'vehicles'>('products');
  const [excelImportBlock, setExcelImportBlock] = useState<'products' | 'vehicles'>('products');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const queryPreselectionHandled = useRef(false);
  const createMethodsRef = useRef<HTMLDivElement>(null);
  const createBeadRef = useRef<HTMLSpanElement>(null);
  const createBeadHaloRef = useRef<HTMLSpanElement>(null);
  const createBeadFrameRef = useRef<number | null>(null);
  const createModeRef = useRef<CreateTestMode>('manual');
  const createBeadMotionRef = useRef({
    initialized: false,
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
    velocityX: 0,
    velocityY: 0,
    lastTime: 0,
  });
  createModeRef.current = createMode;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        testsResponse,
        usersResponse,
        attemptsResponse,
        assignmentsResponse,
        subgroupResponse,
      ] = await Promise.all([
        api.get('/admin/tests'),
        api.get('/admin/users', { params: { limit: 1000 } }),
        api.get('/admin/attempts', { params: { limit: 5000 } }),
        api.get('/admin/test-assignments'),
        api.get<string[]>('/admin/test-subgroups'),
      ]);
      setTests(testsResponse.data || []);
      setUsers(usersResponse.data || []);
      setAttempts(attemptsResponse.data?.attempts || []);
      setAssignments(assignmentsResponse.data || []);
      setTestSubgroupOptions(subgroupResponse.data || [...TEST_SUBGROUP_OPTIONS]);
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Не удалось загрузить данные для назначений'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get('create') === '1') setCreateModalOpen(true);
  }, [searchParams]);

  const moveCreateBead = useCallback((mode: CreateTestMode, instant = false) => {
    const container = createMethodsRef.current;
    const bead = createBeadRef.current;
    const halo = createBeadHaloRef.current;
    const targetButton = container?.querySelector<HTMLButtonElement>(`[data-create-mode="${mode}"]`);
    const targetIcon = targetButton?.querySelector<HTMLElement>('.test-create-method-icon');
    if (!container || !bead || !halo || !targetIcon) return;

    const containerRect = container.getBoundingClientRect();
    const iconRect = targetIcon.getBoundingClientRect();
    const motion = createBeadMotionRef.current;
    motion.targetX = iconRect.left - containerRect.left + container.scrollLeft;
    motion.targetY = iconRect.top - containerRect.top + container.scrollTop;

    if (createBeadFrameRef.current != null) {
      window.cancelAnimationFrame(createBeadFrameRef.current);
      createBeadFrameRef.current = null;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const applyTransform = (stretchX = 0, stretchY = 0) => {
      bead.style.transform = `translate3d(${motion.x}px, ${motion.y}px, 0) scale(${1 + stretchX}, ${1 + stretchY})`;
      halo.style.transform = `translate3d(${motion.x}px, ${motion.y}px, 0)`;
    };

    if (!motion.initialized || instant || reduceMotion) {
      motion.initialized = true;
      motion.x = motion.targetX;
      motion.y = motion.targetY;
      motion.velocityX = 0;
      motion.velocityY = 0;
      motion.lastTime = 0;
      bead.style.transformOrigin = '50% 50%';
      applyTransform();
      return;
    }

    motion.lastTime = 0;
    const tick = (now: number) => {
      const deltaSeconds = motion.lastTime
        ? Math.min(0.032, Math.max(0.008, (now - motion.lastTime) / 1000))
        : 1 / 60;
      motion.lastTime = now;

      const displacementX = motion.x - motion.targetX;
      const displacementY = motion.y - motion.targetY;
      const stiffness = 210;
      const damping = 18;
      motion.velocityX += (-stiffness * displacementX - damping * motion.velocityX) * deltaSeconds;
      motion.velocityY += (-stiffness * displacementY - damping * motion.velocityY) * deltaSeconds;
      motion.x += motion.velocityX * deltaSeconds;
      motion.y += motion.velocityY * deltaSeconds;

      const horizontalMotion = Math.abs(motion.velocityX) >= Math.abs(motion.velocityY);
      const speed = Math.hypot(motion.velocityX, motion.velocityY);
      const stretch = Math.min(0.3, speed * 0.0026);
      const stretchX = horizontalMotion ? stretch : -stretch * 0.42;
      const stretchY = horizontalMotion ? -stretch * 0.42 : stretch;
      bead.style.transformOrigin = horizontalMotion
        ? (motion.velocityX >= 0 ? '12% 50%' : '88% 50%')
        : (motion.velocityY >= 0 ? '50% 12%' : '50% 88%');
      applyTransform(stretchX, stretchY);

      const settled = Math.abs(displacementX) < 0.12
        && Math.abs(displacementY) < 0.12
        && Math.abs(motion.velocityX) < 8
        && Math.abs(motion.velocityY) < 8;
      if (settled) {
        motion.x = motion.targetX;
        motion.y = motion.targetY;
        motion.velocityX = 0;
        motion.velocityY = 0;
        bead.style.transformOrigin = '50% 50%';
        applyTransform();
        createBeadFrameRef.current = null;
        return;
      }
      createBeadFrameRef.current = window.requestAnimationFrame(tick);
    };
    createBeadFrameRef.current = window.requestAnimationFrame(tick);
  }, []);

  useLayoutEffect(() => {
    if (!createModalOpen) {
      createBeadMotionRef.current.initialized = false;
      if (createBeadFrameRef.current != null) {
        window.cancelAnimationFrame(createBeadFrameRef.current);
        createBeadFrameRef.current = null;
      }
      return;
    }
    const frame = window.requestAnimationFrame(() => moveCreateBead(createMode));
    return () => window.cancelAnimationFrame(frame);
  }, [createModalOpen, createMode, moveCreateBead]);

  useEffect(() => {
    if (!createModalOpen || !createMethodsRef.current) return;
    const observer = new ResizeObserver(() => moveCreateBead(createModeRef.current, true));
    observer.observe(createMethodsRef.current);
    return () => observer.disconnect();
  }, [createModalOpen, moveCreateBead]);

  useEffect(() => {
    if (!tests.length || queryPreselectionHandled.current) return;
    queryPreselectionHandled.current = true;
    const queryTestId = Number(searchParams.get('test'));
    if (Number.isFinite(queryTestId) && tests.some((test) => test.id === queryTestId)) {
      setSelectedTestIds([queryTestId]);
    }
  }, [searchParams, tests]);

  const employees = useMemo(() => users.filter(
    (user) => user.role === 'employee' && user.is_active
  ), [users]);

  const employeeById = useMemo(() => new Map(employees.map((user) => [user.id, user])), [employees]);
  const employeeIds = useMemo(() => new Set(employees.map((user) => user.id)), [employees]);

  const latestCompletedAttemptByPair = useMemo(() => {
    const result = new Map<string, AdminAttempt>();
    attempts.forEach((attempt) => {
      if (!attempt.is_completed || !employeeIds.has(Number(attempt.user_id))) return;
      const key = `${attempt.user_id}:${attempt.test_id}`;
      const current = result.get(key);
      const attemptDate = dayjs(attempt.completed_at || attempt.started_at).valueOf();
      const currentDate = current
        ? dayjs(current.completed_at || current.started_at).valueOf()
        : Number.NEGATIVE_INFINITY;
      if (!current || attemptDate > currentDate || (attemptDate === currentDate && attempt.id > current.id)) {
        result.set(key, attempt);
      }
    });
    return result;
  }, [attempts, employeeIds]);

  const failedAttemptsByUser = useMemo(() => {
    const result = new Map<number, AdminAttempt[]>();
    latestCompletedAttemptByPair.forEach((attempt) => {
      if (attempt.is_passed !== false) return;
      const current = result.get(attempt.user_id) || [];
      current.push(attempt);
      result.set(attempt.user_id, current);
    });
    return result;
  }, [latestCompletedAttemptByPair]);

  const latestActiveAssignmentByPair = useMemo(() => {
    const result = new Map<string, AdminAssignment>();
    assignments.forEach((assignment) => {
      const status = normalize(assignment.status);
      if (!assignment.is_active || assignment.completed_at || !['assigned', 'started'].includes(status)) return;
      const key = `${assignment.user_id}:${assignment.test_id}`;
      const current = result.get(key);
      const assignedAt = dayjs(assignment.assigned_at).valueOf();
      const currentAssignedAt = current ? dayjs(current.assigned_at).valueOf() : Number.NEGATIVE_INFINITY;
      if (!current || assignedAt > currentAssignedAt || (assignedAt === currentAssignedAt && assignment.id > current.id)) {
        result.set(key, assignment);
      }
    });
    return result;
  }, [assignments]);

  const retakeStateByPair = useMemo(() => {
    const result = new Map<string, 'needed' | 'assigned'>();
    latestCompletedAttemptByPair.forEach((attempt, key) => {
      if (attempt.is_passed !== false) return;
      const activeAssignment = latestActiveAssignmentByPair.get(key);
      const failedAt = dayjs(attempt.completed_at || attempt.started_at).valueOf();
      const reassignedAt = activeAssignment
        ? dayjs(activeAssignment.assigned_at).valueOf()
        : Number.NEGATIVE_INFINITY;
      result.set(key, reassignedAt > failedAt ? 'assigned' : 'needed');
    });
    return result;
  }, [latestActiveAssignmentByPair, latestCompletedAttemptByPair]);

  const failedUserIdsByTest = useMemo(() => {
    const result = new Map<number, Set<number>>();
    latestCompletedAttemptByPair.forEach((attempt) => {
      if (attempt.is_passed !== false || retakeStateByPair.get(`${attempt.user_id}:${attempt.test_id}`) !== 'needed') return;
      const current = result.get(attempt.test_id) || new Set<number>();
      current.add(attempt.user_id);
      result.set(attempt.test_id, current);
    });
    return result;
  }, [latestCompletedAttemptByPair, retakeStateByPair]);

  const activeAssignmentsByUser = useMemo(() => {
    const result = new Map<number, number>();
    assignments.forEach((assignment) => {
      const status = normalize(assignment.status);
      if (!assignment.is_active || assignment.completed_at || !['assigned', 'started'].includes(status)) return;
      result.set(assignment.user_id, (result.get(assignment.user_id) || 0) + 1);
    });
    return result;
  }, [assignments]);

  const selectedTestSet = useMemo(() => new Set(selectedTestIds), [selectedTestIds]);
  const selectedUserSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const getRelevantFailures = useCallback((userId: number) => {
    const failures = failedAttemptsByUser.get(userId) || [];
    const relevant = selectedTestSet.size
      ? failures.filter((attempt) => selectedTestSet.has(attempt.test_id))
      : failures;
    return relevant.filter((attempt) => (
      retakeStateByPair.get(`${attempt.user_id}:${attempt.test_id}`) === 'needed'
    ));
  }, [failedAttemptsByUser, retakeStateByPair, selectedTestSet]);

  const getRelevantAssignedRetakes = useCallback((userId: number) => {
    const failures = failedAttemptsByUser.get(userId) || [];
    const relevant = selectedTestSet.size
      ? failures.filter((attempt) => selectedTestSet.has(attempt.test_id))
      : failures;
    return relevant.filter((attempt) => (
      retakeStateByPair.get(`${attempt.user_id}:${attempt.test_id}`) === 'assigned'
    ));
  }, [failedAttemptsByUser, retakeStateByPair, selectedTestSet]);

  const retakeCandidateIds = useMemo(() => employees
    .filter((user) => getRelevantFailures(user.id).length > 0)
    .map((user) => user.id), [employees, getRelevantFailures]);

  const retakeCandidateSet = useMemo(() => new Set(retakeCandidateIds), [retakeCandidateIds]);

  const retakeTests = useMemo(() => tests
    .map((test) => ({
      test,
      userIds: Array.from(failedUserIdsByTest.get(test.id) || []),
    }))
    .filter(({ userIds }) => userIds.length > 0)
    .sort((left, right) => right.userIds.length - left.userIds.length
      || left.test.title.localeCompare(right.test.title, 'ru')), [failedUserIdsByTest, tests]);

  const visibleRetakeTests = useMemo(() => {
    const query = normalize(retakeTestSearch);
    return query
      ? retakeTests.filter(({ test }) => normalize(`${test.title} ${getTestCategory(test)}`).includes(query))
      : retakeTests;
  }, [retakeTestSearch, retakeTests]);

  const testCategories = useMemo(() => {
    const values = new Set(tests.map(getTestCategory).filter(Boolean));
    return Array.from(values).sort((left, right) => left.localeCompare(right, 'ru'));
  }, [tests]);

  const filteredTests = useMemo(() => {
    const query = normalize(testSearch);
    return tests.filter((test) => {
      if (testCategory === 'retakes' && !failedUserIdsByTest.get(test.id)?.size) return false;
      const matchesCategory = testCategory === 'all' || getTestCategory(test) === testCategory;
      if (testCategory !== 'retakes' && !matchesCategory) return false;
      if (!query) return true;
      return normalize([
        test.title,
        test.description,
        test.subgroup,
        test.block === 'vehicles' ? 'модели автомобилей' : 'ассортимент запчасти',
        ...(test.question_texts || []),
      ].join(' ')).includes(query);
    });
  }, [failedUserIdsByTest, testCategory, testSearch, tests]);

  const regions = useMemo(() => Array.from(new Set(
    employees.map((user) => user.region?.trim()).filter((value): value is string => Boolean(value))
  )).sort((left, right) => left.localeCompare(right, 'ru')), [employees]);

  const filteredEmployees = useMemo(() => {
    const query = normalize(employeeSearch);
    return employees.filter((user) => {
      if (regionFilter !== 'all' && user.region !== regionFilter) return false;
      if (retakeOnly && !retakeCandidateSet.has(user.id)) return false;
      if (!query) return true;
      return normalize([user.full_name, user.position, user.department, user.region, user.email].join(' ')).includes(query);
    });
  }, [employeeSearch, employees, regionFilter, retakeCandidateSet, retakeOnly]);

  const departmentGroups = useMemo<DepartmentGroup[]>(() => {
    const grouped = new Map<string, User[]>();
    filteredEmployees.forEach((user) => {
      const department = user.department?.trim() || 'Без отдела';
      const current = grouped.get(department) || [];
      current.push(user);
      grouped.set(department, current);
    });
    return Array.from(grouped.entries())
      .map(([name, departmentUsers]) => ({
        name,
        users: departmentUsers.sort((left, right) => left.full_name.localeCompare(right.full_name, 'ru')),
        regions: Array.from(new Set(departmentUsers.map((user) => user.region || 'Регион не указан'))),
      }))
      .sort((left, right) => left.name.localeCompare(right.name, 'ru'));
  }, [filteredEmployees]);

  const selectedTests = useMemo(() => tests.filter((test) => selectedTestSet.has(test.id)), [selectedTestSet, tests]);
  const selectedUsers = useMemo(() => employees.filter((user) => selectedUserSet.has(user.id)), [employees, selectedUserSet]);

  const retakeAssignmentsCount = useMemo(() => selectedUserIds.reduce((sum, userId) => (
    sum + selectedTestIds.filter((testId) => (
      retakeStateByPair.get(`${userId}:${testId}`) === 'needed'
    )).length
  ), 0), [retakeStateByPair, selectedTestIds, selectedUserIds]);

  const activePairCount = useMemo(() => {
    const selectedUsersSet = new Set(selectedUserIds);
    const selectedTestsSet = new Set(selectedTestIds);
    return assignments.filter((assignment) => (
      selectedUsersSet.has(assignment.user_id)
      && selectedTestsSet.has(assignment.test_id)
      && assignment.is_active
      && !assignment.completed_at
      && ['assigned', 'started'].includes(normalize(assignment.status))
    )).length;
  }, [assignments, selectedTestIds, selectedUserIds]);

  const toggleTest = (testId: number) => {
    setSelectedTestIds((current) => current.includes(testId)
      ? current.filter((id) => id !== testId)
      : [...current, testId]);
  };

  const toggleUser = (userId: number) => {
    setSelectedUserIds((current) => current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId]);
  };

  const toggleDepartment = (departmentUsers: User[]) => {
    const userIds = departmentUsers.map((user) => user.id);
    const allSelected = userIds.every((id) => selectedUserSet.has(id));
    setSelectedUserIds((current) => {
      const next = new Set(current);
      userIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return Array.from(next);
    });
  };

  const selectRetakeCandidates = () => {
    if (selectedTestIds.length !== 1) {
      message.warning('Для пересдачи выберите один тест');
      return;
    }
    if (!retakeCandidateIds.length) {
      message.success('По выбранному тесту пересдача никому не требуется');
      return;
    }
    setSelectedUserIds(retakeCandidateIds);
    setRetakeOnly(true);
    message.info(`Выбрано на пересдачу: ${retakeCandidateIds.length}`);
  };

  const prepareRetakeForTest = (testId: number, userIds: number[]) => {
    setSelectedTestIds([testId]);
    setSelectedUserIds(userIds);
    setTestSearch('');
    setTestCategory('retakes');
    setEmployeeSearch('');
    setRegionFilter('all');
    setRetakeOnly(true);
  };

  const openConfirmation = () => {
    if (!selectedTestIds.length) {
      message.warning('Выберите хотя бы один тест');
      return;
    }
    if (!selectedUserIds.length) {
      message.warning('Выберите хотя бы одного сотрудника');
      return;
    }
    setConfirmOpen(true);
  };

  const createAssignments = async () => {
    setSubmitting(true);
    let successfulBatches = 0;
    const errors: string[] = [];
    const dueAt = deadline ? deadline.endOf('day').toISOString() : null;

    for (const testId of selectedTestIds) {
      const assignmentBatches = [
        {
          userIds: selectedUserIds.filter((userId) => (
            retakeStateByPair.get(`${userId}:${testId}`) !== 'needed'
          )),
          allowRetake: false,
        },
        {
          userIds: selectedUserIds.filter((userId) => (
            retakeStateByPair.get(`${userId}:${testId}`) === 'needed'
          )),
          allowRetake: true,
        },
      ].filter((batch) => batch.userIds.length > 0);

      for (const batch of assignmentBatches) {
        try {
          await api.post('/admin/test-assignments', {
            test_id: testId,
            user_ids: batch.userIds,
            due_at: dueAt,
            is_required: true,
            is_active: publishNow,
            ...(batch.allowRetake ? { allow_retake: true } : {}),
          });
          successfulBatches += 1;
        } catch (error: any) {
          const testTitle = tests.find((test) => test.id === testId)?.title || `Тест ${testId}`;
          errors.push(`${testTitle}: ${getApiErrorMessage(error, 'ошибка назначения')}`);
        }
      }
    }

    setSubmitting(false);
    if (successfulBatches > 0) {
      setConfirmOpen(false);
      setSelectedUserIds([]);
      await fetchData();
    }
    if (!errors.length) {
      message.success(
        retakeAssignmentsCount
          ? `Назначения созданы. На пересдачу отправлено: ${retakeAssignmentsCount}`
          : 'Назначения успешно созданы'
      );
      return;
    }
    if (successfulBatches > 0) {
      message.warning(`Часть назначений создана. Ошибок: ${errors.length}`);
    } else {
      message.error(errors[0]);
    }
  };

  const resetSelection = () => {
    setSelectedTestIds([]);
    setSelectedUserIds([]);
    setRetakeOnly(false);
  };

  const resetCreateDraft = () => {
    setCreateMode('manual');
    setTextImportValue('');
    setTextImportBlock('products');
    setExcelImportBlock('products');
    setExcelFile(null);
    manualTestForm.resetFields();
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setCreateModalOpen(false);
    resetCreateDraft();
    if (searchParams.has('create')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('create');
      setSearchParams(nextParams, { replace: true });
    }
  };

  const finishCreatedTest = async (testId: number, successMessage: string) => {
    message.success(successMessage);
    setCreateModalOpen(false);
    resetCreateDraft();
    await fetchData();
    navigate(`/admin/tests/${testId}/questions`);
  };

  const handleCreateTest = async () => {
    if (createMode === 'ai') {
      setCreateModalOpen(false);
      resetCreateDraft();
      navigate('/admin/ai-generator');
      return;
    }

    setCreateSubmitting(true);
    try {
      if (createMode === 'manual') {
        const values = await manualTestForm.validateFields();
        const response = await api.post('/admin/tests', {
          ...values,
          title: values.title.trim(),
          description: values.description?.trim() || null,
          subgroup: values.subgroup?.trim() || null,
        });
        await finishCreatedTest(
          response.data.id,
          `Черновик «${response.data.title}» создан — добавьте вопросы`
        );
        return;
      }

      if (createMode === 'text') {
        if (!textImportValue.trim()) {
          message.warning('Вставьте текст с вопросами');
          return;
        }
        const response = await api.post('/admin/tests/create-from-text', {
          text: textImportValue.trim(),
          block: textImportBlock,
        });
        await finishCreatedTest(
          response.data.test_id,
          `Тест «${response.data.test_title}» создан: ${response.data.questions_created} вопросов`
        );
        return;
      }

      if (!excelFile) {
        message.warning('Выберите Excel-файл для импорта');
        return;
      }
      const formData = new FormData();
      formData.append('file', excelFile);
      const response = await api.post(
        `/admin/tests/import-excel?block=${excelImportBlock}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      message.success(`Импортировано тестов: ${response.data.tests_created || 0} · вопросов: ${response.data.questions_created || 0}`);
      setCreateModalOpen(false);
      resetCreateDraft();
      if (searchParams.has('create')) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('create');
        setSearchParams(nextParams, { replace: true });
      }
      await fetchData();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, 'Не удалось создать тест'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const downloadExcelTemplate = async () => {
    try {
      const response = await api.get('/admin/tests/export-empty-template', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'test_template_empty.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Не удалось скачать шаблон'));
    }
  };

  const excelUploadProps: UploadProps = {
    accept: '.xlsx,.xls',
    maxCount: 1,
    beforeUpload: (file) => {
      setExcelFile(file);
      return false;
    },
    onRemove: () => {
      setExcelFile(null);
    },
  };

  const handleCreateModeKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, mode: CreateTestMode) => {
    const currentIndex = CREATE_TEST_MODE_ORDER.indexOf(mode);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + CREATE_TEST_MODE_ORDER.length) % CREATE_TEST_MODE_ORDER.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % CREATE_TEST_MODE_ORDER.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = CREATE_TEST_MODE_ORDER.length - 1;
    if (nextIndex == null) return;
    event.preventDefault();
    const nextMode = CREATE_TEST_MODE_ORDER[nextIndex];
    setCreateMode(nextMode);
    createMethodsRef.current
      ?.querySelector<HTMLButtonElement>(`[data-create-mode="${nextMode}"]`)
      ?.focus();
  };

  const assignmentCount = selectedTestIds.length * selectedUserIds.length;

  return (
    <Layout wide>
      <div className="assignment-page">
        <div className="assignment-page-header">
          <div>
            <Title level={1}>Назначение тестов</Title>
            <Paragraph>Выберите тесты, затем отделы или отдельных сотрудников.</Paragraph>
          </div>
          <div className="assignment-header-actions">
            <Button type="primary" icon={<EditOutlined />} onClick={() => navigate('/admin/tests')}>Управление тестами</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>Создать тест</Button>
            <div className="assignment-steps" aria-label="Этапы назначения">
              <span className={selectedTestIds.length ? 'done' : 'active'}><b>{selectedTestIds.length ? <CheckOutlined /> : 1}</b>Тесты</span>
              <i />
              <span className={selectedTestIds.length ? (selectedUserIds.length ? 'done' : 'active') : ''}><b>{selectedUserIds.length ? <CheckOutlined /> : 2}</b>Сотрудники</span>
              <i />
              <span className={selectedTestIds.length && selectedUserIds.length ? 'active' : ''}><b>3</b>Параметры</span>
            </div>
          </div>
        </div>

        <section className="assignment-section" aria-labelledby="assignment-tests-title">
          <div className="assignment-section-header">
            <div>
              <Space size={10} align="center">
                <Title level={3} id="assignment-tests-title">Тесты</Title>
                <Tag color="blue">Выбрано: {selectedTestIds.length}</Tag>
              </Space>
              <Text type="secondary">Нажмите на плитку, чтобы добавить тест в назначение.</Text>
            </div>
            <Space wrap>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Поиск теста или категории"
                value={testSearch}
                onChange={(event) => setTestSearch(event.target.value)}
                className="assignment-search"
              />
              <Button type="link" onClick={() => setSelectedTestIds([])} disabled={!selectedTestIds.length}>
                Снять выбор
              </Button>
            </Space>
          </div>

          <div className="assignment-filter-row" aria-label="Категории тестов">
            <Button
              shape="round"
              type={testCategory === 'all' ? 'primary' : 'default'}
              onClick={() => setTestCategory('all')}
            >
              Все тесты
            </Button>
            <Button
              shape="round"
              danger
              type={testCategory === 'retakes' ? 'primary' : 'default'}
              icon={<RetweetOutlined />}
              onClick={() => setTestCategory('retakes')}
            >
              Нужна пересдача · {retakeTests.length}
            </Button>
            {testCategories.map((category) => (
              <Button
                key={category}
                shape="round"
                type={testCategory === category ? 'primary' : 'default'}
                onClick={() => setTestCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          {loading ? (
            <Row gutter={[16, 16]}>
              {[0, 1, 2, 3].map((item) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={item}><Card><Skeleton active paragraph={{ rows: 3 }} /></Card></Col>
              ))}
            </Row>
          ) : filteredTests.length ? (
            <Row gutter={[16, 16]}>
              {filteredTests.map((test) => {
                const selected = selectedTestSet.has(test.id);
                const failedCount = failedUserIdsByTest.get(test.id)?.size || 0;
                const questionsCount = test.questions_count || 0;
                return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={test.id}>
                    <Card
                      hoverable
                      className={`assignment-test-card${selected ? ' selected' : ''}`}
                      onClick={() => toggleTest(test.id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleTest(test.id);
                        }
                      }}
                    >
                      <div className="assignment-test-card-top">
                        <Space size={4} wrap>
                          <Tag color={test.block === 'vehicles' ? 'green' : 'blue'}>{getTestCategory(test)}</Tag>
                          {test.difficulty
                            ? <Tag color={TEST_DIFFICULTY_COLOR[test.difficulty]}>{TEST_DIFFICULTY_LABEL[test.difficulty]}</Tag>
                            : <Tag>Сложность не указана</Tag>}
                        </Space>
                        <span className={`assignment-select-mark${selected ? ' selected' : ''}`} aria-hidden="true">
                          {selected && <CheckOutlined />}
                        </span>
                      </div>
                      <Title level={4} title={test.title}>{test.title}</Title>
                      <Paragraph ellipsis={{ rows: 2 }}>
                        {test.description?.trim() || 'Описание теста не заполнено.'}
                      </Paragraph>
                      <div className="assignment-test-meta">
                        <Space size={7} split={<span className="assignment-meta-dot" />}>
                          <span><FileTextOutlined /> {questionsCount} {getQuestionWord(questionsCount)}</span>
                          <span><ClockCircleOutlined /> {test.time_limit_minutes} мин</span>
                        </Space>
                        <Space size={6} wrap>
                          {failedCount > 0 && (
                            <Tag color="error" icon={<RetweetOutlined />}>Пересдача: {failedCount}</Tag>
                          )}
                          <Tag color={test.is_active ? 'success' : 'default'}>
                            {test.is_active ? 'В общем каталоге' : 'Только по назначению'}
                          </Tag>
                        </Space>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={testCategory === 'retakes' && !testSearch ? 'Сейчас нет тестов для пересдачи' : 'Тесты не найдены'}
            >
              {!testSearch && testCategory === 'all' && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                  Создать первый тест
                </Button>
              )}
            </Empty>
          )}
        </section>

        <section className="assignment-section assignment-employees-section" aria-labelledby="assignment-employees-title">
          <div className="assignment-section-header">
            <div>
              <Space size={10} align="center">
                <Title level={3} id="assignment-employees-title">Сотрудники по отделам</Title>
                <Tag color="blue">Выбрано: {selectedUserIds.length}</Tag>
              </Space>
              <Text type="secondary">Выберите весь отдел или отдельных сотрудников внутри блока.</Text>
            </div>
            <Space wrap>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Найти сотрудника"
                value={employeeSearch}
                onChange={(event) => setEmployeeSearch(event.target.value)}
                className="assignment-search"
              />
              <Select
                value={regionFilter}
                onChange={setRegionFilter}
                className="assignment-region-select"
                options={[
                  { value: 'all', label: 'Все регионы' },
                  ...regions.map((region) => ({ value: region, label: region })),
                ]}
              />
              <Button
                danger
                type={retakeOnly ? 'primary' : 'default'}
                icon={<RetweetOutlined />}
                onClick={() => setRetakeOnly((current) => !current)}
              >
                На пересдачу · {retakeCandidateIds.length}
              </Button>
              <Button type="link" onClick={() => setSelectedUserIds([])} disabled={!selectedUserIds.length}>
                Снять выбор
              </Button>
            </Space>
          </div>

          {!loading && (
            <div className="assignment-retake-panel">
              <div className="assignment-retake-panel-header">
                <div>
                  <Text strong><RetweetOutlined /> Тесты для пересдачи <Tag color="error">{retakeTests.length}</Tag></Text>
                  <Text type="secondary">Последняя попытка не сдана, повторное назначение ещё не создано.</Text>
                </div>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Найти тест для пересдачи"
                  aria-label="Найти тест для пересдачи"
                  value={retakeTestSearch}
                  onChange={(event) => setRetakeTestSearch(event.target.value)}
                  className="assignment-retake-search"
                />
              </div>
              {visibleRetakeTests.length ? (
                <div className="assignment-retake-list">
                  {visibleRetakeTests.map(({ test, userIds }) => {
                    const names = userIds.map((id) => employeeById.get(id)?.full_name).filter(Boolean).join(', ');
                    const selected = selectedTestIds.length === 1
                      && selectedTestIds[0] === test.id
                      && selectedUserIds.length === userIds.length
                      && userIds.every((id) => selectedUserSet.has(id));
                    return (
                      <div className={`assignment-retake-row${selected ? ' selected' : ''}`} key={test.id}>
                        <div className="assignment-retake-row-copy">
                          <Text strong title={test.title}>{test.title}</Text>
                          <Tooltip title={names}>
                            <Text type="secondary" ellipsis>{names}</Text>
                          </Tooltip>
                        </div>
                        <Tag color="error">{userIds.length} чел.</Tag>
                        <Button
                          type={selected ? 'primary' : 'default'}
                          danger
                          aria-label={`Выбрать тест «${test.title}» и ${userIds.length} сотрудников для пересдачи`}
                          onClick={() => prepareRetakeForTest(test.id, userIds)}
                        >
                          {selected ? 'Выбрано' : 'Выбрать тест и сотрудников'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={retakeTests.length ? 'По запросу тесты не найдены' : 'Сейчас нет тестов, ожидающих пересдачи'}
                />
              )}
              {selectedTestIds.length === 1 && (
                <div className="assignment-retake-panel-footer">
                  <Text type="secondary">Красным отмечены сотрудники, не сдавшие выбранный тест.</Text>
                  <Button danger icon={<RetweetOutlined />} disabled={!retakeCandidateIds.length} onClick={selectRetakeCandidates}>
                    Выбрать на пересдачу · {retakeCandidateIds.length}
                  </Button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <Row gutter={[18, 18]}><Col span={24}><Card><Skeleton active paragraph={{ rows: 8 }} /></Card></Col></Row>
          ) : departmentGroups.length ? (
            <div className="assignment-department-grid">
              {departmentGroups.map((department) => {
                const departmentUserIds = department.users.map((user) => user.id);
                const selectedInDepartment = departmentUserIds.filter((id) => selectedUserSet.has(id)).length;
                const allSelected = selectedInDepartment === departmentUserIds.length;
                const partiallySelected = selectedInDepartment > 0 && !allSelected;
                const retakeInDepartment = departmentUserIds.filter((id) => retakeCandidateSet.has(id)).length;
                return (
                  <Card
                    key={department.name}
                    className={`assignment-department-card${allSelected ? ' selected' : ''}`}
                    title={(
                      <div
                        className="assignment-department-title"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleDepartment(department.users)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleDepartment(department.users);
                          }
                        }}
                      >
                        <Checkbox checked={allSelected} indeterminate={partiallySelected} tabIndex={-1} />
                        <span className="assignment-department-icon"><TeamOutlined /></span>
                        <span className="assignment-department-copy">
                          <strong>{department.name}</strong>
                          <small>{department.regions.join(' · ')}</small>
                        </span>
                        {retakeInDepartment > 0 && <Tag color="error">Пересдача: {retakeInDepartment}</Tag>}
                        <Tag>{selectedInDepartment}/{department.users.length}</Tag>
                      </div>
                    )}
                  >
                    <div className="assignment-employee-grid">
                      {department.users.map((employee) => {
                        const selected = selectedUserSet.has(employee.id);
                        const failures = getRelevantFailures(employee.id);
                        const needsRetake = failures.length > 0;
                        const assignedRetakes = getRelevantAssignedRetakes(employee.id);
                        const activeCount = activeAssignmentsByUser.get(employee.id) || 0;
                        const failureTitles = failures.map((attempt) => attempt.test_title || `Тест ${attempt.test_id}`);
                        const latestFailure = [...failures].sort((left, right) => (
                          dayjs(right.completed_at || right.started_at).valueOf()
                          - dayjs(left.completed_at || left.started_at).valueOf()
                        ))[0];
                        return (
                          <div
                            key={employee.id}
                            className={`assignment-employee-card${selected ? ' selected' : ''}${needsRetake ? ' needs-retake' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-pressed={selected}
                            onClick={() => toggleUser(employee.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleUser(employee.id);
                              }
                            }}
                          >
                            <Avatar>{getInitials(employee.full_name)}</Avatar>
                            <div className="assignment-employee-copy">
                              <Text strong ellipsis title={employee.full_name}>{employee.full_name}</Text>
                              <Text type="secondary" ellipsis title={employee.position || undefined}>
                                {employee.position || 'Должность не указана'}
                              </Text>
                              <Space size={6} wrap className="assignment-employee-status">
                                <span>{employee.region || 'Регион не указан'}</span>
                                {needsRetake ? (
                                  <Tooltip title={failureTitles.join(', ')}>
                                    <Tag color="error" icon={<WarningOutlined />}>
                                      Пересдача{latestFailure?.score != null ? ` · ${latestFailure.score.toFixed(0)}%` : ''}
                                    </Tag>
                                  </Tooltip>
                                ) : assignedRetakes.length > 0 ? (
                                  <Tooltip title={assignedRetakes.map((attempt) => attempt.test_title || `Тест ${attempt.test_id}`).join(', ')}>
                                    <Tag color="warning" icon={<RetweetOutlined />}>Пересдача назначена</Tag>
                                  </Tooltip>
                                ) : activeCount > 0 ? (
                                  <Tag color="processing">Активных: {activeCount}</Tag>
                                ) : (
                                  <Tag color="success">Свободен</Tag>
                                )}
                              </Space>
                            </div>
                            <span className={`assignment-employee-check${selected ? ' selected' : ''}`}>
                              {selected && <CheckOutlined />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={retakeOnly ? 'Сотрудники на пересдачу не найдены' : 'Сотрудники не найдены'}
            />
          )}
        </section>

        <div className="assignment-summary-bar">
          <div className="assignment-summary-main">
            <UsergroupAddOutlined />
            <div>
              <Text strong>
                Выбрано: {selectedTestIds.length} тестов · {selectedUserIds.length} сотрудников
              </Text>
              <Text type="secondary">
                Будет создано назначений: {assignmentCount}
                {retakeAssignmentsCount > 0 ? ` · пересдач: ${retakeAssignmentsCount}` : ''}
              </Text>
            </div>
          </div>
          <div className="assignment-summary-divider" />
          <div className="assignment-deadline-field">
            <Text type="secondary">Срок выполнения</Text>
            <DatePicker
              value={deadline}
              onChange={setDeadline}
              format="DD.MM.YYYY"
              suffixIcon={<CalendarOutlined />}
              disabledDate={(date) => date.startOf('day').isBefore(dayjs().startOf('day'))}
              allowClear
            />
          </div>
          <label className="assignment-publish-control">
            <Switch checked={publishNow} onChange={setPublishNow} />
            <span><Text strong>Сразу открыть сотрудникам</Text><Text type="secondary">Иначе назначение сохранится подготовленным — выдадите позже</Text></span>
          </label>
          <Button onClick={resetSelection} disabled={!selectedTestIds.length && !selectedUserIds.length}>
            Сбросить
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<UsergroupAddOutlined />}
            disabled={!selectedTestIds.length || !selectedUserIds.length}
            onClick={openConfirmation}
          >
            Назначить
          </Button>
        </div>
      </div>

      <Modal
        title="Создание теста"
        open={createModalOpen}
        onCancel={closeCreateModal}
        onOk={handleCreateTest}
        okText={createMode === 'ai'
          ? 'Открыть AI-генератор'
          : createMode === 'excel'
            ? 'Импортировать'
            : createMode === 'text'
              ? 'Создать и проверить вопросы'
              : 'Создать и добавить вопросы'}
        cancelText="Отмена"
        confirmLoading={createSubmitting}
        width={820}
        destroyOnClose
      >
        <Paragraph type="secondary" className="test-create-intro">
          Выберите удобный способ. Новый тест сохраняется как черновик и не появится у сотрудников до публикации.
        </Paragraph>

        <div ref={createMethodsRef} className="test-create-methods" role="tablist" aria-label="Способ создания теста">
          <span ref={createBeadHaloRef} className="test-create-bead-halo" aria-hidden="true" />
          <span ref={createBeadRef} className="test-create-bead" aria-hidden="true" />
          {[
            { key: 'manual', icon: <EditOutlined />, title: 'Пустой тест', text: 'Название и вопросы вручную' },
            { key: 'text', icon: <FileTextOutlined />, title: 'Из текста', text: 'Распознать вопросы и ответы' },
            { key: 'excel', icon: <CloudUploadOutlined />, title: 'Из Excel', text: 'Импортировать готовый файл' },
            ...(AI_TEST_GENERATION_ENABLED
              ? [{ key: 'ai', icon: <RobotOutlined />, title: 'С помощью AI', text: 'Сгенерировать по теме' }]
              : []),
          ].map((method) => (
            <button
              type="button"
              role="tab"
              aria-selected={createMode === method.key}
              tabIndex={createMode === method.key ? 0 : -1}
              data-create-mode={method.key}
              className={`test-create-method${createMode === method.key ? ' selected' : ''}`}
              key={method.key}
              onClick={() => setCreateMode(method.key as CreateTestMode)}
              onKeyDown={(event) => handleCreateModeKeyDown(event, method.key as CreateTestMode)}
            >
              <span className="test-create-method-icon">{method.icon}</span>
              <strong>{method.title}</strong>
              <small>{method.text}</small>
            </button>
          ))}
        </div>

        <div className="test-create-content" key={createMode}>
          {createMode === 'manual' && (
            <Form
              form={manualTestForm}
              layout="vertical"
              initialValues={{ block: 'products', time_limit_minutes: 30 }}
              requiredMark={false}
            >
              <Form.Item
                name="title"
                label="Название теста"
                rules={[
                  { required: true, whitespace: true, message: 'Укажите название теста' },
                  { max: 200, message: 'Не более 200 символов' },
                ]}
              >
                <Input autoFocus placeholder="Например: Амортизаторы — базовые знания" showCount maxLength={200} />
              </Form.Item>
              <Form.Item name="description" label="Описание">
                <TextArea placeholder="Что проверяет тест и для кого он предназначен" rows={3} maxLength={500} showCount />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} md={7}>
                  <Form.Item name="block" label="Раздел" rules={[{ required: true }]}>
                    <Select options={[
                      { value: 'products', label: 'Ассортимент и запчасти' },
                      { value: 'vehicles', label: 'Модели автомобилей' },
                    ]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={7}>
                  <Form.Item name="subgroup" label="Категория">
                    <AutoComplete
                      allowClear
                      options={testSubgroupOptions.map((value) => ({ value }))}
                      filterOption={(input, option) => String(option?.value || '').toLocaleLowerCase('ru').includes(input.toLocaleLowerCase('ru'))}
                      placeholder="Выберите или введите"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="difficulty"
                    label="Сложность"
                    rules={[{ required: true, message: 'Укажите сложность' }]}
                  >
                    <Select placeholder="Выберите" options={TEST_DIFFICULTY_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="time_limit_minutes"
                    label="Время"
                    rules={[{ required: true, message: 'Укажите время' }]}
                  >
                    <InputNumber min={1} max={300} addonAfter="мин" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Alert
                type="info"
                showIcon
                icon={<BulbOutlined />}
                message="Следующий шаг — редактор вопросов"
                description="После создания откроется экран, где можно добавить варианты ответов, изображения, пояснения и правильные ответы."
              />
            </Form>
          )}

          {createMode === 'text' && (
            <div className="test-create-text-grid">
              <div>
                <Text strong>Текст с вопросами</Text>
                <TextArea
                  value={textImportValue}
                  onChange={(event) => setTextImportValue(event.target.value)}
                  placeholder={`Тест по амортизаторам\n\n1. Для чего нужен амортизатор?\nа) Для снижения колебаний\nб) Для запуска двигателя\nв) Для охлаждения\n✅ а`}
                  rows={14}
                  className="test-create-textarea"
                />
              </div>
              <div className="test-create-help">
                <Text strong>Как оформить</Text>
                <ol>
                  <li>Первая строка — название теста.</li>
                  <li>Вопрос начинайте с «1.» или «1)».</li>
                  <li>Ответы пишите как «а)», «б)», «в)».</li>
                  <li>Верный вариант отметьте «✅ а».</li>
                </ol>
                <Text type="secondary">После распознавания откроется редактор: проверьте вопросы перед публикацией.</Text>
                <div className="test-create-block-field">
                  <Text strong>Раздел</Text>
                  <Select
                    value={textImportBlock}
                    onChange={setTextImportBlock}
                    options={[
                      { value: 'products', label: 'Ассортимент и запчасти' },
                      { value: 'vehicles', label: 'Модели автомобилей' },
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {createMode === 'excel' && (
            <div className="test-create-excel">
              <Upload.Dragger {...excelUploadProps} fileList={excelFile ? [excelFile as any] : []}>
                <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
                <p className="ant-upload-text">Перетащите Excel-файл сюда или нажмите для выбора</p>
                <p className="ant-upload-hint">Поддерживаются .xlsx и .xls. Можно импортировать один или несколько тестов.</p>
              </Upload.Dragger>
              <div className="test-create-excel-options">
                <div>
                  <Text strong>Раздел для импортируемого теста</Text>
                  <Select
                    value={excelImportBlock}
                    onChange={setExcelImportBlock}
                    options={[
                      { value: 'products', label: 'Ассортимент и запчасти' },
                      { value: 'vehicles', label: 'Модели автомобилей' },
                    ]}
                  />
                </div>
                <Button type="link" onClick={downloadExcelTemplate}>Скачать пустой шаблон Excel</Button>
              </div>
            </div>
          )}

          {createMode === 'ai' && (
            <div className="test-create-ai">
              <span><RobotOutlined /></span>
              <Title level={4}>AI подготовит черновик по теме</Title>
              <Paragraph type="secondary">
                Укажите тему, количество и сложность вопросов. Результат можно проверить и отредактировать перед публикацией.
              </Paragraph>
              <Alert type="warning" showIcon message="AI может допускать ошибки — проверяйте правильные ответы перед назначением." />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Подтвердить назначение"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={createAssignments}
        okText="Создать назначения"
        cancelText="Отмена"
        confirmLoading={submitting}
        width={640}
      >
        <Paragraph type="secondary">Проверьте параметры перед сохранением.</Paragraph>
        <div className="assignment-review-grid">
          <div><Text type="secondary">Тестов</Text><strong>{selectedTests.length}</strong></div>
          <div><Text type="secondary">Сотрудников</Text><strong>{selectedUsers.length}</strong></div>
          <div><Text type="secondary">Назначений</Text><strong>{assignmentCount}</strong></div>
          <div><Text type="secondary">Срок</Text><strong>{deadline ? deadline.format('DD.MM.YYYY') : 'Без срока'}</strong></div>
        </div>
        {retakeAssignmentsCount > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<RetweetOutlined />}
            style={{ marginTop: 16 }}
            message={`На пересдачу: ${retakeAssignmentsCount}`}
            description="Для этих пар «сотрудник — тест» последняя завершённая попытка была неуспешной. Назначение будет открыто повторно."
          />
        )}
        {activePairCount > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={`Уже активных назначений: ${activePairCount}`}
            description="Они не продублируются: срок, статус и дата назначения будут обновлены."
          />
        )}
        <Alert
          type={publishNow ? 'success' : 'info'}
          showIcon
          icon={publishNow ? <CheckOutlined /> : <ReloadOutlined />}
          style={{ marginTop: 12 }}
          message={publishNow
            ? 'Назначения будут сразу видны сотрудникам'
            : 'Назначения сохранятся подготовленными — сотрудники их не увидят, пока вы не откроете доступ'}
        />
      </Modal>
    </Layout>
  );
};

export default AdminAssignments;
